import { Injectable, signal, computed } from '@angular/core';
import { ManagementUser } from '../models/management-user.model';
import { SSOCredentials } from '../models/credentials.model';
import { School } from '../models/school.model';
import {District} from "../models/district.model";
import {Student} from "../models/student.model";

export interface SchoolAdminTeacherFilter {
  teachers: ManagementUser[];
  selectedTeacherId: number | null;
}

@Injectable({
  providedIn: 'root'
})
export class SessionStorageService {
  // TESTING OUT SIGNALS
  // I wanted to test out angular signals for demo mode. We could potentially
  // use signals for everything in this service which would allow us to inject
  // any of these properties into our components and have them react to them without
  // needing to use event emitters (like with the original demo mode implementation).
  private demoModeSignal = signal(false);

  readonly isUserLoggedIn = signal(false);

  // TODO: Move this to a more appropriate service now that it doesn't actually use SessionStorage
  private readonly userDataInternal = signal<ManagementUser | null>(null);
  readonly userData = computed(() => {
    if (!this.isUserLoggedIn()) return null ;

    return this.userDataInternal();
  });


  // Define keys used for user session data to centralize clearing logic
  private readonly USER_SESSION_KEYS = [
    'loginTime',
    'currentRoute',
    'lastAccess',
    'username',
    'password',
    'user',
    'isLoggedIn',
    'sso',
    'sessionExpirationTime', // Will be cleared by clearSessionExpirationTime as well
    'selectedDistrict',
    'selectedSchool',
    'selectedTeacher',
    'districts',
    'schools',
    'teachers',
    'diagnosticOpts',
    'diagnosticStudents',
    'diagnosticDetailsStudents',
    'diagnosticDetailsStudent',
    'resourceCategory',
    'schoolAdminTeacherFilter',
    'gettingStartedCheckedItems',
  ];

  constructor() {
    // Initialize signals from session storage
    // Note: I think we do this a bit more generically. Maybe have a map of keys to signals.
    const demoModeEnabled = sessionStorage.getItem('demoModeEnabled');
    if (demoModeEnabled === 'true') {
      this.demoModeSignal.set(true);
    } else {
      this.demoModeSignal.set(false);
    }

    const userObject = this.getSessionObject('user');
    if (userObject) {
      this.userDataInternal.set(new ManagementUser(userObject));
    }
    this.isUserLoggedIn.set(this.getBoolean('isLoggedIn'));
  }

  /**
   * Read-only isDemoMode signal that can be injected into components. Must be called like
   * a function to get the value.
   *
   * Example component injection:
   * `isDemoMode: Signal<boolean> = inject(SessionStorageService).isDemoMode;`
   *
   * Example usage in component or template:
   * e.g. `isDemoMode()`
   */
  isDemoMode = computed(() => this.demoModeSignal())

  /**
   * Set the demo mode flag. Gets persisted to session storage and updates the signal
   */
  setIsDemoModeEnabled(val: boolean) {
    // Write to session storage
    this.setBoolean('demoModeEnabled', val);
    // Update the signal
    this.demoModeSignal.set(val);
    // Clear out all previous selections when switching demo mode flag
    this.setDistricts([]);
    this.setSchools([]);
    this.setTeachers([]);
    this.clearSelectedDistrict();
    this.clearSelectedSchool();
    this.clearSelectedTeacher();
  }

  /**
   * Toggles the current value of demo mode to be the opposite
   */
  toggleDemoModeEnabled() {
    this.setIsDemoModeEnabled(!this.isDemoMode());
  }

  clearAll() {
    sessionStorage.clear();
    this.demoModeSignal.set(false);
    this.userDataInternal.set(null);
    this.isUserLoggedIn.set(false);
  }

  /**
   * Clears data specific to the user's session, leaving
   * 'logoutMessage' and 'demoModeEnabled' alone.
   *  - Also sets 'isLoggedIn' to false.
   */
  clearUserData() {
    for (const key of this.USER_SESSION_KEYS) {
      sessionStorage.removeItem(key);
    }
    this.setUserLoggedIn(false);
  }

  /**
   * Specifically removes the session expiration time item.
   */
  clearSessionExpirationTime() {
    sessionStorage.removeItem('sessionExpirationTime');
  }


  setLoginTime(login: number) {
    this.setNumber('loginTime', login) ;
  }

  getLoginTime() {
    return this.getNumber('loginTime') ;
  }

  setCurrentRoute(url: string) {
    sessionStorage.setItem('currentRoute', url) ;
  }

  getCurrentRoute(): string | null {
    return sessionStorage.getItem('currentRoute') ;
  }

  setLastAccess(time: number) {
    this.setNumber('lastAccess', time) ;
  }

  getLastAccess(): number | null {
    return this.getNumber('lastAccess') ;
  }

  getUsername(): string | null {
    return sessionStorage.getItem('username') ;
  }

  setUsername(username: string) {
    sessionStorage.setItem('username', username) ;
  }

  getPassword(): string | null {
    return sessionStorage.getItem('password') ;
  }

  setPassword(password: string) {
    sessionStorage.setItem('password', password) ;
  }

  setUserData(userData: ManagementUser) {
    const copy = new ManagementUser(userData);
    this.setSessionObject('user', copy)
    this.userDataInternal.set(copy);
  }

  setUserLoggedIn(isLoggedIn: boolean) {
    this.setBoolean('isLoggedIn', isLoggedIn);
    this.isUserLoggedIn.set(isLoggedIn);
  }

  setSSOInfo(sso: SSOCredentials) {
    this.setSessionObject('sso', sso);
  }

  getSSOInfo(): SSOCredentials | null {
    return this.getSessionObject('sso');
  }

  removeSSOInfo() {
    this.removeSessionObject('sso');
  }

  setSessionExpirationTime(expirationTime: number | null) {
    if (expirationTime == null)
    {
      this.clearSessionExpirationTime();
    }
    else
    {
      this.setNumber('sessionExpirationTime', expirationTime) ;
    }
  }

  getSessionExpirationTime(): number | null {
    return this.getNumber('sessionExpirationTime') ;
  }

  // Reports related data
  getSelectedDistrict(): District | null {
    return this.getSessionObject('selectedDistrict') ;
  }

  setSelectedDistrict(district: District) {
    this.setSessionObject('selectedDistrict', district) ;
  }

  clearSelectedDistrict() {
    this.removeSessionObject('selectedDistrict') ;
  }

  getSelectedSchool(): School | null {
    return this.getSessionObject('selectedSchool') ;
  }

  setSelectedSchool(school: School) {
    this.setSessionObject('selectedSchool', school) ;
  }

  clearSelectedSchool() {
    this.removeSessionObject('selectedSchool') ;
  }

  getSelectedTeacher(): ManagementUser | null {
    return this.getSessionObject('selectedTeacher') ;
  }

  setSelectedTeacher(teacher: ManagementUser) {
    this.setSessionObject('selectedTeacher', teacher) ;
  }

  clearSelectedTeacher() {
    this.removeSessionObject('selectedTeacher') ;
  }

  getDistricts(): District[] | null {
    return this.getSessionObject('districts') ;
  }

  setDistricts(districts: District[]) {
    this.setSessionObject('districts', districts) ;
  }

  getSchools(): School[] | null {
    return this.getSessionObject('schools') ;
  }

  setSchools(schools: School[]) {
    this.setSessionObject('schools', schools) ;
  }

  getTeachers(): ManagementUser[] | null {
    return this.getSessionObject('teachers') ;
  }

  setTeachers(teachers: ManagementUser[]) {
    this.setSessionObject('teachers', teachers) ;
  }

  getDiagnosticOpts(): any {
    return this.getSessionObject('diagnosticOpts') ;
  }

  setDiagnosticOpts(opts: any) {
    this.setSessionObject('diagnosticOpts', opts) ;
  }

  getDiagnosticStudents(): Student[] {
    return this.getSessionObject('diagnosticStudents') ;
  }

  setDiagnosticStudents(students: Student[]) {
    this.setSessionObject('diagnosticStudents', students) ;
  }

  getDiagnosticDetailsStudentIds(): number[] {
    return this.getSessionObject('diagnosticDetailsStudentIds') ;
  }

  setDiagnosticDetailsStudentIds(ids: number[]) {
    this.setSessionObject('diagnosticDetailsStudentIds', ids) ;
  }

  getDiagnosticDetailsStudents(): Student[] {
    return this.getSessionObject('diagnosticDetailsStudents') ;
  }

  setDiagnosticDetailsStudents(students: Student[]) {
    this.setSessionObject('diagnosticDetailsStudents', students) ;
  }

  getDiagnosticDetailsStudentId(): number {
    return this.getSessionObject('diagnosticDetailsStudentId') ;
  }

  setDiagnosticDetailsStudentId(id: number) {
    this.setNumber('diagnosticDetailsStudentId', id);
  }

  getDiagnosticDetailsStudent(): Student {
    return this.getSessionObject('diagnosticDetailsStudent') ;
  }

  setDiagnosticDetailsStudent(student: Student) {
    this.setSessionObject('diagnosticDetailsStudent', student) ;
  }

  getCopyOfSessionData(): string {
    return JSON.stringify(sessionStorage);
  }

  restoreSessionFromCopy(sessionCopy: string) {
    this.clearAll();
    let sessionCopyObject: Object = JSON.parse(sessionCopy)
    Object.keys(sessionCopyObject).forEach(key => {
      const value = (sessionCopyObject as any)[key];
      if (value !== undefined && value !== null) {
           sessionStorage.setItem(key, value);
      }
    });
    const demoModeEnabled = sessionStorage.getItem('demoModeEnabled');
    this.demoModeSignal.set(demoModeEnabled === 'true');
  }

  setResourceCategory(category: string) {
    sessionStorage.setItem('resourceCategory', category)
  };

  getResourceCategory(): string | null{
    return sessionStorage.getItem('resourceCategory')
  }

  setLogoutMessage(msg: string, title?: string) {
    if (msg) {
       sessionStorage.setItem('logoutMessage', msg) ;
       if (title) {
         sessionStorage.setItem('logoutMessageTitle', title) ;
       }
    } else {
       sessionStorage.removeItem('logoutMessage');
       sessionStorage.removeItem('logoutMessageTitle');
    }
  }

  getLogoutMessage(): {msg: string, title: string} | null {
    const msg = sessionStorage.getItem('logoutMessage');
    const title = sessionStorage.getItem('logoutMessageTitle');
    if (msg) {
      return {msg, title: title ?? 'Session Ended'};
    }
    return null;
  }

  setSchoolAdminTeacherFilter(teacherFilter: SchoolAdminTeacherFilter): void {
    this.setSessionObject('schoolAdminTeacherFilter', teacherFilter) ;
  }

  getSchoolAdminTeacherFilter(): SchoolAdminTeacherFilter | null {
    return this.getSessionObject('schoolAdminTeacherFilter') ;
  }

  getGettingStartedCheckedItems(): number[] {
    return this.getSessionObject('gettingStartedCheckedItems') ?? [];
  }

  setGettingStartedCheckedItems(items: number[]): void {
    this.setSessionObject('gettingStartedCheckedItems', items);
  }

  private getBoolean(key: string): boolean {
    return sessionStorage.getItem(key) === 'true';
  }

  private setBoolean(key: string, value: boolean) {
    sessionStorage.setItem(key, value ? 'true' : 'false');
  }

  private getNumber(key: string): number | null {
    let numString = sessionStorage.getItem(key);
    return (numString !== null && numString !== '') ? Number(numString) : null;
  }

  private setNumber(key: string, value: number) {
    sessionStorage.setItem(key, value.toString());
  }

  private getSessionObject(key: string): any {
    let sessionObject = null;
    let sessionObjectString: string | null = sessionStorage.getItem(key);

    if (sessionObjectString && sessionObjectString !== 'undefined' && sessionObjectString !== 'null')
    {
      try {
        sessionObject = JSON.parse(sessionObjectString);
      } catch (e) {
        console.error(`Error parsing session storage key "${key}":`, e);
        sessionStorage.removeItem(key);
        sessionObject = null;
      }
    }

    return sessionObject;
  }

  private setSessionObject(key: string, object: any) {
    if (object === undefined || object === null) {
      this.removeSessionObject(key);
      return;
    }
    try {
      let sessionObjectString = JSON.stringify(object);
      sessionStorage.setItem(key, sessionObjectString);
    } catch (e) {
       console.error(`Error stringifying object for session storage key "${key}":`, e);
    }
  }

  private removeSessionObject(key: string) {
    sessionStorage.removeItem(key);
  }
}
